import { apiFetch } from '@/lib/api'

// ---- Platform Directive --------------------------------------------------

export type DirectiveScope = 'bots' | 'xobstaff' | 'llm_agents' | 'ceo'
export type DirectiveStatus = 'scheduled' | 'active' | 'expired' | 'cancelled'

export interface Directive {
  id: number
  text: string
  scope: DirectiveScope[]
  starts_at: string | null
  ends_at: string | null
  status: DirectiveStatus
  actor: number
  actor_type: string
  note: string
  created_at: string
  is_active_now: boolean
}

export interface DirectiveActiveResponse {
  by_scope: Record<DirectiveScope, { has_active: boolean; preview: string }>
  counts: Record<string, number>
  active: Directive[]
}

export function listDirectives(): Promise<{ results: Directive[] }> {
  return apiFetch('/api/admin/directives/')
}

export function fetchActiveDirectives(): Promise<DirectiveActiveResponse> {
  return apiFetch('/api/admin/directives/active/')
}

export function createDirective(body: {
  text: string
  scope: DirectiveScope[]
  ends_at?: string | null
  note?: string
}): Promise<Directive> {
  return apiFetch('/api/admin/directives/', { method: 'POST', body })
}

export function endDirectiveNow(
  id: number,
  reason?: string
): Promise<Directive> {
  return apiFetch(`/api/admin/directives/${id}/end-now/`, {
    method: 'POST',
    body: { reason: reason ?? '' }
  })
}

export function previewDirective(scope: DirectiveScope): Promise<{
  scope: DirectiveScope
  directive: string
  base_prompt_excerpt: string
  composed_excerpt: string
}> {
  return apiFetch(`/api/admin/directives/preview/?scope=${scope}`)
}

// ---- Bot Army ------------------------------------------------------------

export interface BotsStatus {
  active: number
  actions_last_hour: number
  is_running: boolean | null
}

export function fetchBotsStatus(): Promise<BotsStatus> {
  return apiFetch('/api/admin/bots/status/')
}

export function controlBots(
  op: 'start' | 'stop' | 'cycle',
  durationMinutes?: number
) {
  return apiFetch(`/api/admin/bots/${op}/`, {
    method: 'POST',
    body: op === 'cycle' ? { duration_minutes: durationMinutes ?? null } : {}
  })
}

// ---- Bot Engine Config ---------------------------------------------------

export interface BotEngineConfig {
  today: {
    post: number
    comment: number
    like: number
    repost: number
    reply: number
  }
  llm_tiers: { tier1: number; tier2: number; tier3: number; tier4: number }
  activity_level: 'chill' | 'normal' | 'aggressive'
}

export function fetchBotEngine(): Promise<BotEngineConfig> {
  return apiFetch('/api/admin/bots/engine/')
}

export function patchBotEngine(data: {
  activity_level?: string
  llm_tiers?: Record<string, number>
}): Promise<{ ok: boolean }> {
  return apiFetch('/api/admin/bots/engine/', { method: 'PATCH', body: data })
}

// ---- Bot Directives ------------------------------------------------------

export interface BotDirective {
  id: number
  title: string
  direction: string
  tone: string
  priority: string
  status: string
  posts_target: number
  comments_target: number
  posts_done: number
  comments_done: number
  start_at: string | null
  end_at: string | null
  created_at: string
}

export function listBotDirectives(
  status = 'active'
): Promise<{ results: BotDirective[] }> {
  return apiFetch(`/api/admin/bots/directives/?status=${status}`)
}

export function createBotDirective(data: {
  title: string
  direction: string
  tone: string
  priority: string
  posts_target: number
  comments_target: number
  hours: number
}): Promise<{ ok: boolean; id: number; directive: BotDirective }> {
  return apiFetch('/api/admin/bots/directives/', { method: 'POST', body: data })
}

export function endBotDirective(id: number): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/bots/directives/${id}/`, { method: 'DELETE' })
}

export function burstBotDirective(id: number): Promise<{ ok: boolean }> {
  return apiFetch(`/api/admin/bots/directives/${id}/burst/`, { method: 'POST' })
}

// ---- Payouts -------------------------------------------------------------

export interface PayoutRow {
  id: number
  user: string | null
  user_id: number | null
  amount: number
  status: string | null
  created_at: string | null
}

export interface PayoutsResponse {
  results: PayoutRow[]
  summary: {
    pending_count: number
    failed_count: number
    pending_amount: number
    failed_amount: number
  }
}

export function fetchPayouts(status = 'all'): Promise<PayoutsResponse> {
  const q = status === 'all' ? '' : `?status=${encodeURIComponent(status)}`
  return apiFetch(`/api/admin/payouts/${q}`)
}

export function decidePayout(
  id: number,
  decision: 'approve' | 'fail',
  reason?: string
) {
  return apiFetch(`/api/admin/payouts/${id}/decide/`, {
    method: 'POST',
    body: { decision, reason: reason ?? '' }
  })
}

// ---- Staff Agents --------------------------------------------------------

export interface StaffAgent {
  agent_name: string | null
  model: string | null
  temperature: number
  is_killed: boolean
  today_runs: number
  today_cost_usd: number
}

export function fetchStaffAgents(): Promise<{
  agents: StaffAgent[]
  summary: Record<string, unknown>
}> {
  return apiFetch('/api/admin/agents/staff/')
}

export interface StaffAgentRun {
  id: number
  agent: string | null
  status: string | null
  model: string | null
  tokens_in: number
  tokens_out: number
  cost_usd: number
  duration_ms: number | null
  created_at: string | null
}

export function fetchStaffAgentRuns(
  agent?: string,
  status = 'all'
): Promise<{ results: StaffAgentRun[] }> {
  const q = new URLSearchParams()
  if (agent) q.set('agent', agent)
  if (status && status !== 'all') q.set('status', status)
  const suffix = q.toString() ? `?${q}` : ''
  return apiFetch(`/api/admin/agents/staff/runs/${suffix}`)
}

export function killStaffAgentRun(runId: number) {
  return apiFetch(`/api/admin/agents/staff/runs/${runId}/kill/`, {
    method: 'POST'
  })
}

export function updateStaffAgentConfig(
  name: string,
  body: Record<string, unknown>
) {
  return apiFetch(
    `/api/admin/agents/staff/${encodeURIComponent(name)}/config/`,
    {
      method: 'PATCH',
      body
    }
  )
}

// ---- Scrapers ------------------------------------------------------------

export interface ScraperSourceStatus {
  available: boolean
  running?: number
  failed_last_24h?: number
  last_run_at?: string | null
  last_run_status?: string | null
}

export function fetchScrapersStatus(): Promise<{
  sources: Record<string, ScraperSourceStatus>
}> {
  return apiFetch('/api/admin/scrapers/status/')
}

// ---- LLM Agents ----------------------------------------------------------

export const LLM_AGENT_KINDS = [
  'xob',
  'doctor',
  'lawyer',
  'education',
  'relationship',
  'financial'
] as const
export type LlmAgentKind = (typeof LLM_AGENT_KINDS)[number]

export function toggleLlmAgent(kind: LlmAgentKind, value: boolean) {
  return apiFetch(`/api/admin/llm-agents/${kind}/toggle/`, {
    method: 'POST',
    body: { value }
  })
}

// ---- Email ---------------------------------------------------------------

export function sendTestEmail(body: {
  to: string
  subject?: string
  body?: string
}) {
  return apiFetch('/api/admin/email/test/', { method: 'POST', body })
}

// ---- System Tasks --------------------------------------------------------

export interface SystemTasks {
  queue_depth: number
  redis_ok: boolean
  last_metrics_refresh_at: string | null
}

export function fetchSystemTasks(): Promise<SystemTasks> {
  return apiFetch('/api/admin/system/tasks/')
}

export function refreshMetricsNow() {
  return apiFetch('/api/admin/system/metrics/refresh/', { method: 'POST' })
}

export function fetchLlmAgents(): Promise<{
  agents: Record<LlmAgentKind, boolean>
  enabled: boolean
}> {
  return apiFetch('/api/admin/llm-agents/')
}
